/**
 * EquityChart — lightweight SVG equity curve, no dependencies.
 *
 * Props:
 *   data          [{date, value}]   equity curve points
 *   initialFunds  number            baseline reference value
 *   benchmarkData [{date, value}]   optional index curve, rescaled to the
 *                                   same starting value as `data`, for a
 *                                   side-by-side returns comparison
 *   benchmarkLabel string           legend label for the overlay series
 *                                   (default "Nifty 50")
 *   height        number            SVG height (default 180)
 */
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function valueFormatter(v) {
  if (v >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(1)}Cr`;
  if (v >= 1_00_000)    return `${(v / 1_00_000).toFixed(1)}L`;
  if (v >= 1_000)       return `${(v / 1_000).toFixed(0)}K`;
  return new Intl.NumberFormat('en-IN').format(v).toString();
}

export default function EquityChart({ data = [], initialFunds = 0, benchmarkData = [], benchmarkLabel = "Nifty 50", height = 180 }) {
  if (!data.length) return null;

  const benchmarkByDate = new Map(benchmarkData.map((i) => [i.date, i.value]));
  const hasBenchmark = benchmarkByDate.size > 0;

  const chartdata = data.map((i)=> {
        return {
            "date": i.date,
            "Portfolio": i.value,
            "Initial": initialFunds,
            ...(hasBenchmark ? { [benchmarkLabel]: benchmarkByDate.get(i.date) ?? null } : {}),
        }
    })

    const values = data.map((d) => d.value);
    const final  = values[values.length - 1];
    const equityColour = final >= initialFunds ? "hsl(var(--win))" : "hsl(var(--loss))";

    return (
        <div className="h-60 w-full text-xs"> 
            <ResponsiveContainer>
                <ComposedChart
                data={chartdata}
                margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                >
                    <CartesianGrid 
                      strokeWidth={1} strokeOpacity={0.5}
                      strokeDasharray="3 3" vertical={false}/>
                    
                    <defs>
                        <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={equityColour} stopOpacity={0.6} />
                        <stop offset="95%" stopColor={equityColour} stopOpacity={0.0} />
                        </linearGradient>
                    </defs>

                    <XAxis 
                      dataKey="date"
                      axisLine={false} 
                      dy={10}
                      tick={{fill: 'hsl(var(--foreground))'}}
                      minTickGap={10}
                    />
                    
                    <YAxis
                      axisLine={false} 
                      tickLine={false}
                      tickFormatter={(value)=>valueFormatter(value)}
                      domain={['dataMin', 'auto']}
                      tick={{fill: 'hsl(var(--foreground))'}}
                    />
                    
                    <Tooltip
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                      contentStyle={{background: 'hsl(var(--background))', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />

                    {hasBenchmark && <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />}

                    {/* The Area Chart (Revenue) */}
                    <Area
                        type="monotone"
                        dataKey="Portfolio"
                        fill="url(#chartGradient)"
                        stroke={equityColour}
                        fillOpacity={0.3}
                        formatter={(value)=>new Intl.NumberFormat('en-IN').format(value).toString()}
                    />

                    {/* The Line Chart (Target) */}
                    <Line
                        dataKey="Initial"
                        stroke="hsl(var(--primary))"
                        strokeWidth={1}
                        dot={false}
                        tooltipType="none"
                        strokeDasharray="5"
                        legendType="none"
                    />

                    {/* Optional overlay series (e.g. Nifty 50 or a backtest curve), rescaled to the same starting value */}
                    {hasBenchmark && (
                        <Line
                            type="monotone"
                            dataKey={benchmarkLabel}
                            stroke="hsl(var(--muted-foreground))"
                            strokeWidth={1.5}
                            dot={false}
                            connectNulls
                            formatter={(value)=>new Intl.NumberFormat('en-IN').format(value).toString()}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}
